import React, { useEffect, useRef, useCallback } from 'react';
import websocketService from '../../services/websocketService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import {
  handleQueueRealtimeWebSocketMessage,
  clearQueueRealtimeDebounce,
  isQueueRealtimePayload,
  dispatchQueueRealtimeGlobalEvent,
} from '../../services/queueRealtimeSyncService';
import { dispatchQueueRealtimeNotificationBridge } from '../../services/queueRealtimeNotificationBridge';

/**
 * Single subscription on the shared agent WebSocket for hd-notification queue events.
 * Re-subscribes after reconnect (websocket-connected) and clears debounce on logout.
 */
export default function QueueRealtimeSyncListener() {
  const { isConnected, connectionStatus } = useWebSocket();
  const handlerRef = useRef(null);
  const prevConnRef = useRef(connectionStatus);

  const attach = useCallback(() => {
    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (!token) {
      return;
    }

    if (handlerRef.current) {
      websocketService.unsubscribe('QUEUE_REALTIME_SYNC', handlerRef.current);
      handlerRef.current = null;
    }

    const handler = (msg) => {
      if (!isQueueRealtimePayload(msg)) {
        return;
      }
      // Global channel: notifications/badge/toasts must be page-independent.
      dispatchQueueRealtimeGlobalEvent(msg);
      // Immediate unified notification UX (toast + bell); API refresh remains debounced fallback.
      dispatchQueueRealtimeNotificationBridge(msg);
      // Page-scoped channel: queue/dashboard/report data refresh remains screen-aware.
      handleQueueRealtimeWebSocketMessage(msg);
    };

    handlerRef.current = handler;
    websocketService.subscribe('QUEUE_REALTIME_SYNC', handler);
    // console.log(
    //   JSON.stringify({
    //     ns: 'queue-realtime',
    //     stage: 'subscription_attached',
    //     ts: new Date().toISOString(),
    //   })
    // );
  }, []);

  useEffect(() => {
    if (isConnected) {
      attach();
    }
    return () => {
      if (handlerRef.current) {
        websocketService.unsubscribe('QUEUE_REALTIME_SYNC', handlerRef.current);
        handlerRef.current = null;
      }
    };
  }, [isConnected, attach]);

  useEffect(() => {
    const onWsConnected = () => attach();
    const onLogout = () => {
      if (handlerRef.current) {
        websocketService.unsubscribe('QUEUE_REALTIME_SYNC', handlerRef.current);
        handlerRef.current = null;
      }
      clearQueueRealtimeDebounce();
      // console.log(
      //   JSON.stringify({
      //     ns: 'queue-realtime',
      //     stage: 'logout_cleanup',
      //     ts: new Date().toISOString(),
      //   })
      // );
    };
    window.addEventListener('websocket-connected', onWsConnected);
    window.addEventListener('user-logged-out', onLogout);
    return () => {
      window.removeEventListener('websocket-connected', onWsConnected);
      window.removeEventListener('user-logged-out', onLogout);
    };
  }, [attach]);

  useEffect(() => {
    const prev = prevConnRef.current;
    if (connectionStatus === 'connected' && prev !== 'connected') {
      //console.log(
      //   JSON.stringify({
      //     ns: 'queue-realtime',
      //     stage: 'websocket_context_connected',
      //     previousStatus: prev,
      //     ts: new Date().toISOString(),
      //   })
      // );
    }
    if ((connectionStatus === 'disconnected' || connectionStatus === 'error') && prev === 'connected') {
      //console.log(
      //   JSON.stringify({
      //     ns: 'queue-realtime',
      //     stage: 'websocket_context_disconnected',
      //     connectionStatus,
      //     ts: new Date().toISOString(),
      //   })
      // );
    }
    prevConnRef.current = connectionStatus;
  }, [connectionStatus]);

  return null;
}
